import { useSafeNavigate } from '@/services/router/safe-navigate';
import { activeRouteStore } from '@customerio/hydra-nav-state';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useStore } from '@nanostores/react';

import { useCallback, useMemo, useEffect, useState, useRef } from 'react';
import { useActiveBundle } from '../../../../context/active-bundle';
import { useCurrentWorkspace } from '../../../../context/current-workspace';
import { AppBundle } from '../../../../utils/app-bundles';
import type { NavItem as NavItemType, NavItemGroup } from './routes';
import { generateRoutes, isNavItemActive } from './routes';
import { isEmptyString } from '@/utils/string';

import { shouldProcessLinkClick, useIsSettings } from '../../../../utils/navigation';

import useSetupGuideExperiment from '@/features/experiments/setup-guide/hook';
import { useListWorkspaceObjectTypes } from '@/features/workspaces/hooks';
import { useAuth } from '@/services/auth';
import { AccountState } from '@/services/auth/types';
import { useSampleWorkspace } from '@/features/workspaces/sample-workspace';
import { useAnalytics } from '@/services/analytics';
import {
  SideNavGroup,
  NavItem,
  NavItemExpandable,
  NavItemExpandableTrigger,
  NavItemExpandableContent,
  NavItemDropdown,
  NavItemDropdownTrigger,
  NavItemDropdownContent,
  Box,
  Label,
  InlineStack,
  useSideNavState,
} from '@customerio/pluma-components/react';

function ExtendedNavItem(props: {
  item: NavItemType;
  onClick: (
    event: React.MouseEvent<HTMLAnchorElement>,
    item: NavItemType,
    section: string | null,
    parentItem: string | null,
  ) => unknown;
  section: string | null;
  parentItem?: string | null;
}) {
  const { item, onClick, section, parentItem = null } = props;

  const activeRoute = useStore(activeRouteStore);

  const { iconName, href, label, id, showPremiumLabel = false, showNewLabel = false, children, ariaLabel } = item;

  if (item.isDropdownMenu === true && children != null) {
    if (children.length === 0) {
      return null;
    }

    if (children.length === 1) {
      return <ExtendedNavItem item={children[0]} onClick={onClick} section={section} parentItem={item.label} />;
    }

    return (
      <NavItemDropdown>
        <NavItemDropdownContent>
          {children.map((child) => (
            <ExtendedNavItem key={child.id} item={child} onClick={onClick} section={section} parentItem={item.label} />
          ))}
        </NavItemDropdownContent>

        <NavItemDropdownTrigger aria-label={ariaLabel} icon={iconName}>
          {label}
        </NavItemDropdownTrigger>
      </NavItemDropdown>
    );
  }

  if (children != null) {
    return (
      <NavItemExpandable key={item.id}>
        <NavItemExpandableTrigger icon={iconName}>{label}</NavItemExpandableTrigger>

        <NavItemExpandableContent>
          {children.map((child) => {
            return (
              <ExtendedNavItem
                key={child.id}
                item={child}
                onClick={onClick}
                section={section}
                parentItem={item.label}
              />
            );
          })}
        </NavItemExpandableContent>
      </NavItemExpandable>
    );
  }

  return (
    <NavItem
      data-test-nav-item={id}
      href={href}
      icon={iconName}
      isSelected={isNavItemActive(item, activeRoute)}
      onClick={(event) => onClick(event as any, item, section, parentItem)}
    >
      <InlineStack gap="050" alignBlock="center">
        <Box as="span" truncate={true}>
          {label}
        </Box>
        {showNewLabel ? (
          <Label color="purple" flexShrink="0">
            New
          </Label>
        ) : null}
        {showPremiumLabel ? (
          <Label color="plum" flexShrink="0">
            Premium
          </Label>
        ) : null}
      </InlineStack>
    </NavItem>
  );
}

export function MainNavigation() {
  const currentWorkspace = useCurrentWorkspace();
  const permissions = usePermissions();
  const auth = useAuth();
  const safeNavigate = useSafeNavigate();
  const activeApp = useActiveBundle();
  const { isSampleWorkspace } = useSampleWorkspace();
  const journeysObjectTypes = useListWorkspaceObjectTypes(currentWorkspace?.id);
  const isTrial = auth.account.account_state === AccountState.Free;
  const { track } = useAnalytics();
  const sideNavState = useSideNavState();
  const isCollapsedRef = useRef(sideNavState?.isCollapsed === true);
  isCollapsedRef.current = sideNavState?.isCollapsed === true;

  // setup guide nav setup
  const [isSetupGuideComplete, setIsSetupGuideComplete] = useState(!isTrial);

  const { isLoading: isSetupGuideLoading, setupGuide } = useSetupGuideExperiment();
  const { isSettingsPage } = useIsSettings();

  useEffect(() => {
    if (isSetupGuideLoading) return;
    if (activeApp === AppBundle.Journeys && setupGuide.completed_at == null) {
      setIsSetupGuideComplete(false);
      return;
    }
    if (setupGuide.completed_at != null && !isSetupGuideComplete) {
      setIsSetupGuideComplete(true);
    }
  }, [currentWorkspace?.id, activeApp, isSetupGuideComplete, isSetupGuideLoading, setupGuide?.completed_at]);

  const onClickNavItem = useCallback(
    (
      event: React.MouseEvent<HTMLAnchorElement>,
      item: NavItemType,
      section: string | null,
      parentItem: string | null,
    ) => {
      if (!shouldProcessLinkClick(event)) return;

      event.preventDefault();

      void track('leftNav', 'itemClicked', {
        'Nav Item': item.label,
        'Nav Section': section ?? null,
        'Is Nested': parentItem != null,
        'Parent Item': parentItem ?? null,
        'Entry Point': isCollapsedRef.current ? 'collapsed_icon' : 'expanded',
      });

      if (item.href == null) return;

      safeNavigate(item.href, item.transitionOptions ?? {});
    },
    [safeNavigate, track],
  );

  // generating navigation items and groups
  const navItemGroups = useMemo(() => {
    let items: NavItemGroup[] = [];

    if (currentWorkspace != null) {
      const objectTypes = journeysObjectTypes.data ?? [];
      items = generateRoutes(currentWorkspace, auth, permissions, {
        shouldShowHome: !isSetupGuideComplete && !isSampleWorkspace(currentWorkspace.id),
        objectTypes,
      });
    }

    if (
      isSettingsPage &&
      auth.user?.last_cio_workspace_id != null &&
      !isEmptyString(auth.user?.last_cio_workspace_id) &&
      currentWorkspace != null
    ) {
      items = generateRoutes(currentWorkspace, auth, permissions);
    }

    const groups = items.map((group, index) => {
      const navItems = group.items.map((item) => {
        return <ExtendedNavItem key={item.id} item={item} onClick={onClickNavItem} section={group.title ?? null} />;
      });

      return (
        <SideNavGroup key={`group_${index}`} title={group.title}>
          {navItems}
        </SideNavGroup>
      );
    });

    return groups;
  }, [
    auth,
    isSampleWorkspace,
    currentWorkspace,
    permissions,
    isSettingsPage,
    isSetupGuideComplete,
    journeysObjectTypes.data,
    onClickNavItem,
  ]);

  return <>{navItemGroups}</>;
}
